import {
  Button,
  Divider,
  SvgIcon,
  Typography,
  Box,
  Grid,
  Popper,
  Paper,
  ListItem,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import { useStyles } from "./nav-banner-style";
import { MenuIcon } from "utils/constants";
import { MenuData } from "utils/types";
import SettingsEthernetOutlinedIcon from "@mui/icons-material/SettingsEthernetOutlined";
import { useNavigate } from "react-router-dom";
import { RenderHTML } from "components/sanitizeHtml/sanitizeHtml";
import { List } from "@mui/material";
import { handleMenuKeyDown } from "utils/helpers";

export interface NavBannerProps {
  welcomeMessage: string;
  menuList: MenuData[];
}

const SubjectArea = "subjectarea";

export const NavBanner = ({ welcomeMessage, menuList }: NavBannerProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const listSubjectId = "list-Subjects";
  const id = open ? "simple-popover" : undefined;
  const buttonId = "btnCourseFinder";

  const listRefs = useRef(new Map());
  const visibleIds = menuList
    .map((x) => x.menuList)
    .flat()
    .map((x) => x.id);

  const handleOpen = () => {
    setOpen(true);
  };

  const modifiers = [
    {
      name: "flip",
      enabled: false,
    },
  ];

  const handlePopperClose = (event) => {
    if (anchorRef.current?.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  const findCurrentActiveElement = () => listRefs.current[visibleIds[selectedIndex]];
  const handlePopperKeyDown = (e: {
    key: string;
    shiftKey: boolean;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) =>
    handleMenuKeyDown(
      e,
      handleClose,
      visibleIds,
      buttonId,
      setSelectedIndex,
      findCurrentActiveElement,
    );

  const handleClick = (id: string) => {
    setOpen(false);
    navigate(`/${SubjectArea}/` + id);
  };

  useEffect(() => {
    const handleFocusOut = (e) => {
      if (!e.relatedTarget?.closest(`#${listSubjectId}`)) {
        setOpen(false);
      }
    };

    window.addEventListener("focusout", handleFocusOut);
    return () => {
      window.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  useEffect(() => {
    if (open) {
      const element = document.querySelector(`#${listSubjectId} > li`)
        ?.firstChild as HTMLLinkElement;
      element?.focus();
      setSelectedIndex(0);
    } 
  }, [open]);

  useEffect(() => {
    const element = findCurrentActiveElement()?.querySelector("a");
    element?.focus();
  }, [selectedIndex]);

  const isSelected = (id: string) => visibleIds[selectedIndex] === id;

  return (
    <Box>
      <Button
        id={buttonId}
        aria-controls={open ? id : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        variant="text"
        disableElevation
        onClick={handleToggle}
        onMouseOver={handleOpen}
        className={classes.btnCourseFinder}
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        onMouseLeave={handleClose}
        ref={anchorRef}
        aria-labelledby={buttonId + " " + id}
      >
        {t("course_finder")}
      </Button>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal={true}
        placement="bottom"
        modifiers={modifiers}
        className={classes.popper}
        aria-label="Popup"
      >
        <Paper
          elevation={24}
          className={classes.paper}
          onMouseLeave={handlePopperClose}
          onMouseEnter={handleOpen}
          onKeyDown={handlePopperKeyDown}
        >
          <Box className={classes.containerDiv}>
            <Box role="dialog" className={classes.menulistContainer}>
              <Typography variant="h2" className={classes.heading}>
                {t("available_subjects")}
              </Typography>
              <Grid container>
                {menuList.map((menu, index) => (
                  <Box key={index}>
                    {process.env.REACT_APP_SHOW_NON_GROUPED_MENU.toString() === "false" && (
                      <Grid size={{ xs: 12, md: 6, lg: menuList.length >= 3 ? 4 : 12 }}>
                        <Box className={classes.titleContainer}>
                          <SvgIcon
                            component={MenuIcon[menu.header] ?? SettingsEthernetOutlinedIcon}
                            className={classes.menuIconStyle}
                          ></SvgIcon>
                          <Typography className={classes.menuHeader}>{menu.header}</Typography>
                        </Box>

                        <Divider className={classes.divider} />
                        <List id={listSubjectId}>
                          {menu.menuList.map((item) => (
                            <ListItem
                              key={item.id}
                              ref={(el) => {
                                listRefs.current[item.id] = el;
                              }}
                              onClick={() => handleClick(item.id)}
                              onMouseDown={(e) => e.preventDefault()}
                              className={
                                isSelected(item.id)
                                  ? classes.menuListItem + " " + classes.focus
                                  : classes.menuListItem
                              }
                            >
                              <RenderLink item={item} className={classes.hyperLink} />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}
                    {process.env.REACT_APP_SHOW_NON_GROUPED_MENU.toString() === "true" && (
                      <List className={classes.nonGroupedList} id={listSubjectId}>
                        {menu.menuList.map((item) => (
                          <ListItem
                            key={item.id}
                            ref={(el) => {
                              listRefs.current[item.id] = el;
                            }}
                            onClick={() => handleClick(item.id)}
                            onMouseDown={(e) => e.preventDefault()}
                            className={
                              isSelected(item.id)
                                ? classes.nonGroupedMenuItem + " " + classes.focus
                                : classes.nonGroupedMenuItem
                            }
                          >
                            <RenderLink item={item} className={classes.hyperLink} />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                ))}
              </Grid>
            </Box>
            <Box className={classes.descriptionContainer}>
              <Box className={classes.descriptionTitleContainer}>
                <Box className={classes.iconContainer}>
                  <SvgIcon
                    component={ExploreOutlinedIcon}
                    className={classes.svgIconStyle}
                  ></SvgIcon>
                </Box>
                <Typography variant="h3" className={classes.LeapHeading}>
                  {t("discover_leap")}
                </Typography>
              </Box>
              <Box className={classes.dividerContainer}>
                <Divider></Divider>
              </Box>
              <Box className={classes.content}>
                <RenderHTML content={welcomeMessage} />
              </Box>
            </Box>
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};

const RenderLink = ({ item, className }) => {
  return (
    <a
      href={`/${SubjectArea}/` + item.id}
      onClick={(event) => {
        event.preventDefault();
      }}
      className={className}
    >
      {item.name}
    </a>
  );
};
